import React, { useState, useCallback } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import { useDocuments } from 'contexts/documents'
import { compact, prune } from 'utilities/array'
import { getDate, getTime } from 'utilities/date-time'
import Modal from 'modals/dialog'
import Form from 'components/form/controller'
import StringField from 'components/form/field/string'
import { Helper } from 'components/form/field/s'
import OneOfField from 'components/form/field/one-of'
import RadiobuttonsField from 'components/form/field/radiobuttons'
import Message from 'components/message'
import Status from './status'
import Actions from './actions'

const BulkEditDocumentName = ({ act, failed, progress, dismiss, salt }) => {
    const { formatMessage } = useIntl()

    const { getEditableSelection } = useDocuments()
    const selection = getEditableSelection()

    let extension = 'mix'

    const byUsage = (progress.total === 1) ?
        { [selection[0].name]: 1 } :
        selection.reduce((accumulator, { name }) => {
            if(!accumulator[name]) {
                accumulator[name] = 1
            } else {
                accumulator[name]++
            }

            return accumulator
        }, {})

    const mostCommon = (progress.total === 1) ?
        { value: selection[0].name, count: 1 } :
        Object.entries(byUsage)
            .reduce((accumulator, [value, count]) => {
                if(count > accumulator.count) {
                    return {
                        value,
                        count
                    }
                }

                return accumulator
            }, { value: '', count: 0 })

    const [name, setName] = useState(((mostCommon.count > 1 || progress.total === 1) && mostCommon.count / progress.total >= .5) ?
        mostCommon.value :
        ''
    )

    const [nameFormatExtendMode, setNameFormatExtendMode] = useState('append')

    const extensions = prune(selection.map(({ extension }) => extension || 'smart'))
    if(extensions.length === 1) {
        extension = extensions[0]
    }

    const extendName = useCallback(
        getNameExtender(name, { append: nameFormatExtendMode === 'append' }),
        [name, nameFormatExtendMode]
    )

    const autoIncrementName = useCallback(
        getNameAutoIncrementer(name, { selectionCount: progress.total }),
        [name, progress.total]
    )

    const dateStampName = useCallback(
        getNameDateStamper(name, {
            timeOptions: {
                precision: 'seconds',
                units: 'seconds' // 12:34:56
            }
        }),
        [name]
    )

    const rename = ({ format }, ...rest) => {
        const performat = {
            autoIncrement: autoIncrementName,
            dateStamp: dateStampName,
            extend: extendName
        }[format]

        const names = selection.reduce((accumulator, dokument, index) => ({
            ...accumulator,
            [dokument.id]: performat(dokument, index)
        }), {})

        act({ names }, ...rest)
    }

    return (
        <Form
            layout="vertical"
            onSubmit={rename}
            key={`${salt}:bulk:name`}>
            <Modal.Body>
                <StringField
                    salt={salt}
                    label={formatMessage({
                        id: 'noun_name',
                        defaultMessage: 'Name'
                    })}
                    name="name"
                    field={{
                        value: name,
                        suffix: extension ? `.${extension}` : null,
                        required: true
                    }}
                    controlProps={{ autoFocus: true }}
                    onChange={({ name }) => setName(name)}
                    enabled={progress.processed === 0} />
                {(extension === 'mix') && (
                    <Helper>
                        <FormattedMessage
                            id="documents_bulk_edit_name_mixed_extensions_helper"
                            defaultMessage="The selected documents have different extensions. Only their names will be changed." />
                    </Helper>
                )}
                <OneOfField
                    salt={salt}
                    label={formatMessage({
                        id: 'documents_bulk_edit_name_format',
                        defaultMessage: 'Format'
                    })}
                    name="format"
                    field={{
                        value: 'autoIncrement',
                        required: true,
                        include: 'always',
                        conscious: true,
                        options: [
                            {
                                value: 'autoIncrement',
                                label: formatMessage({
                                    id: 'documents_bulk_edit_name_format_auto_increment',
                                    defaultMessage: 'Append incrementing number'
                                }),
                                content: (
                                    <Message
                                        type="info"
                                        className="preserve-line-breaks"
                                        message={formatMessage({
                                            id: 'documents_bulk_edit_name_format_auto_increment_description',
                                            defaultMessage: 'Each document will have an incrementing number added to the end of the name you’ve specified, in order to avoid name collisions.\n\nExample: <strong>{example}<\/strong>'
                                        }, {
                                            example: autoIncrementName(selection[0], 0, true)
                                        })} />
                                )
                            },
                            {
                                value: 'dateStamp',
                                label: formatMessage({
                                    id: 'documents_bulk_edit_name_format_datestamp',
                                    defaultMessage: 'Date stamp'
                                }),
                                content: (
                                    <Message
                                        type="info"
                                        className="preserve-line-breaks"
                                        message={formatMessage({
                                            id: 'documents_bulk_edit_name_format_datestamp_description',
                                            defaultMessage: 'Each document will have the date stamp for when it was uploaded appended to the name you’ve specified, in order to avoid name collisions.\n\nExample: <strong>{example}<\/strong>'
                                        }, {
                                            example: dateStampName(selection[0], 0, true)
                                        })} />
                                )
                            },
                            {
                                value: 'extend',
                                label: formatMessage({
                                    id: 'documents_bulk_edit_name_format_extend',
                                    defaultMessage: 'Extend'
                                }),
                                content: (
                                    <>
                                        <Message
                                            type="info"
                                            className="preserve-line-breaks"
                                            message={formatMessage({
                                                id: 'documents_bulk_edit_name_format_extend_description',
                                                defaultMessage: 'Each document will have its current name extended with the name you’ve specified.\n\nExample: <strong>{example}<\/strong>'
                                            }, {
                                                example: extendName(selection[0], 0, true)
                                            })} />
                                        <RadiobuttonsField
                                            salt={salt}
                                            label={false}
                                            name="nameFormatExtendMode"
                                            field={{
                                                value: nameFormatExtendMode,
                                                include: 'always'
                                            }}
                                            options={[
                                                {
                                                    value: 'append',
                                                    name: formatMessage({
                                                        id: 'documents_bulk_edit_name_format_extend_append',
                                                        defaultMessage: 'Current name first'
                                                    })
                                                },
                                                {
                                                    value: 'prepend',
                                                    name: formatMessage({
                                                        id: 'documents_bulk_edit_name_format_extend_prepend',
                                                        defaultMessage: 'Current name last'
                                                    })
                                                }
                                            ]}
                                            onChange={({ nameFormatExtendMode: mode }) => setNameFormatExtendMode(mode)}
                                            enabled={progress.processed === 0} />
                                    </>
                                )
                            }
                        ]
                    }}
                    enabled={progress.processed === 0} />
                <Status
                    progress={progress}
                    failed={failed} />
            </Modal.Body>
            <Actions
                progress={progress}
                submissible={!!name}
                dismiss={dismiss} />
        </Form>
    )
}

const getNameAutoIncrementer = (name, options = {}) => (dokument, index, withExtension = false) => {
    const { selectionCount = 2 } = options

    return compact([
        name?.trim(),
        ' ',
        `${index + 1}`.padStart(`${selectionCount}`.length, '0'),
        withExtension && `.${dokument.extension || 'smart'}`
    ]).join('')
}

const getNameDateStamper = (name, options = {}) => ({ uploadedAt, extension }, index, withExtension = false) => {
    const date = new Date(uploadedAt)
    const { timeOptions = {} } = options

    return compact([
        name?.trim(),
        ' ',
        getDate(date),
        ' ',
        getTime(date, timeOptions),
        withExtension && `.${extension || 'smart'}`
    ]).join('')
}

const getNameExtender = (name, options = {}) => (dokument, index, withExtension = false) => {
    const { append = false } = options

    return compact([
        append && `${dokument.name} ${name.trimStart()}`.trim(),
        !append && `${name.trimEnd()} ${dokument.name}`.trim(),
        withExtension && `.${dokument.extension || 'smart'}`
    ]).join('')
}

export default BulkEditDocumentName